/* eslint-disable space-infix-ops */
import { color, Common, device, Notation } from '@stockbit/ui';
import styled, { css } from 'styled-components';
import { STREAM_TYPE } from '../constants';
import {
  AdvancedSearchWrapperProps,
  NavigationContainerProps,
  OldSwitcherContainerProps,
  SearchBoxContainerProps,
} from './types';

const { Text, Flex, DatePicker, PopoverContentWrapper, Input } = Common;
const { Tag } = Notation;

/**
 *
 * NAVIGATION AREA
 *
 */

const WebContainer = styled.div`
  display: none;

  @media ${device.tablet} {
    align-items: center;
    display: flex;
    flex: 1;
    justify-content: space-between;
  }

  @media ${device.laptop} {
    flex: 0;
    justify-content: flex-start;
  }
`;

const StreamTypeWeb = styled(Text).attrs({ as: 'button' })`
  font-size: 14px;
  padding: 3px 8px;
  color: ${color.midgray};
  border: none;
  background-color: ${color.light};
  cursor: pointer;
  ${({ active }) =>
    active &&
    css`
      color: ${color.nightRider};
      border: 1px solid ${color.veryLightGrey};
      border-radius: 5px;
    `};
  :focus {
    outline: 0;
  }
`;

const DropdownOption = styled(Text)`
  display: block;
  text-decoration: none;
  line-height: 18px;
  padding: 8px;
  margin: 0;
  white-space: nowrap;
  font-size: 14px;
  color: ${color.suvaGrey};
  cursor: pointer;
  :hover {
    background-color: ${color.aliceBlue};
  }

  ${({ active }) =>
    active &&
    css`
      color: ${color.primary};
    `}
`;

const MobileContainer = styled.div`
  display: block;

  @media ${device.tablet} {
    display: none;
  }
`;

const DropdContainer = styled(Flex)`
  align-items: center;
  width: fit-content;
  button {
    min-height: 23px;
    padding-left: 4px;
    white-space: nowrap;
    font-size: 14px;
    color: ${color.primary};
    border: none;
    background-color: ${color.light};
    cursor: pointer;
    :focus {
      outline: 0;
    }
  }
`;

const PopoverContainer = styled.div`
  border: 1px solid #ededed;
  box-shadow: 0 1px 8px 1px rgb(0 0 0 / 6%);
  border-radius: 4px;
  min-width: 160px;
  padding: 8px;
`;

/**
 *
 * SEARCH AREA
 *
 */

const InputDate = styled(DatePicker)`
  height: 30px;
  margin-left: 10px;
`;

const AdvancedSearchWrapper = styled.div<AdvancedSearchWrapperProps>`
  display: none;
  ${({ active }) =>
    active &&
    css`
      border: 1px solid ${color.gainsboro};
      border-top: none;
      padding: 12px 15px;
      display: block;
    `}
`;

const SearchInput = styled(Input)`
  border: 1px solid ${color.downgray};
  padding: 5px 15px;

  :hover {
    border-color: ${color.downgray} !important;
  }

  ::placeholder {
    color: ${color.downgray} !important;
    text-transform: capitalize;
  }

  input::placeholder,
  input::-webkit-input-placeholder {
    color: #e4e4e4 !important;
    text-transform: capitalize;
  }
`;

/**
 *
 * CONTAINER AREA
 *
 */

const SearchBoxContainer = styled.div<SearchBoxContainerProps>`
  overflow: hidden;
  height: 0;
  transition: all 0.3s ease-in-out;

  ${({ active }) =>
    active &&
    css`
      margin-top: 20px;
      margin-bottom: 16px;
      height: max-content;
      overflow: unset;
    `}
`;
const OldSwitcherContainer = styled.div<OldSwitcherContainerProps>`
  background-image: ${({ backgroundImage }) => backgroundImage || ''};
  height: 90px;
  background-size: 130px;
  background-repeat: no-repeat;
  background-position: 20px 10px;
  padding: 15px 0 15px 180px;
  margin: 15px 20px;
  border-bottom: 1px solid ${color.gainsboro};
  white-space: nowrap;
`;

const NewSwitcherContainer = styled(Flex)`
  padding: 10px 10px 0 10px;
`;

const NavigationContainer = styled.div<NavigationContainerProps>`
  padding: 5px 20px;

  ${({ streamType, category }) =>
    streamType === STREAM_TYPE.SYMBOL && category !== 'notes' &&
    css`
      border-bottom: 1px solid ${color.gainsboro};
    `}
`;

const FlexScrollable = styled(Flex)`
  overflow-x: scroll;
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
    width: 0px;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  scrollbar-width: none;
`;

const NavigationTabContainer = styled(FlexScrollable)`
  align-items: center;
  justify-content: space-between;
  height: 56px;
  margin: 0 -20px;
  padding: 0 20px;
  border-bottom: solid 1px ${color.gainsboro};
`;

const StreamMenuContainer = styled(FlexScrollable)`
  align-items: center;
  justify-content: space-between;
  margin: 15px 0;
  overflow-x: scroll;
`;

const DropdownWrapper = styled(PopoverContentWrapper)`
  border: 1px solid #ededed;
  -webkit-box-shadow: 0 1px 8px 1px rgb(0 0 0 / 6%);
  box-shadow: 0 1px 8px 1px rgb(0 0 0 / 6%);
  border-radius: 3px;
  min-width: 160px;
  padding: 8px;
`;

const NewIndicator = styled(Tag)<{ larger?: boolean }>`
  color: #ffffff;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #00ab6b;
  border: 1px solid #00ab6b;
  font-size: 10px;
  padding: 2px;
  margin-left: -4px;

  ${({ larger = false }) =>
    larger &&
    css`
      width: 20px;
      height: 20px;
    `}
`;

export {
  WebContainer,
  StreamTypeWeb,
  DropdownWrapper,
  DropdownOption,
  DropdContainer,
  InputDate,
  AdvancedSearchWrapper,
  SearchInput,
  SearchBoxContainer,
  OldSwitcherContainer,
  NewSwitcherContainer,
  MobileContainer,
  NavigationContainer,
  NavigationTabContainer,
  StreamMenuContainer,
  NewIndicator,
  PopoverContainer,
};
